import moment from 'moment'
import {Metadata, SEOPositionable} from '@/modules/seo/types'
import KeyValuePair from '@/modules/core/support/KeyValuePair'
import { MediaObject } from '@/modules/media/types'

/**
 * Publicación del blog
 */
export class Post implements SEOPositionable {
    public id: number = 0
    public createdAt: number = 0
    public updatedAt: number = 0
    public author = {id: 0, name: ''}
    public translations: PostTranslation[] = []
    public tags: string[] = []
    public categories: Category[] = []
    public seo: Metadata = new Metadata()
    public featuredImage = new MediaObject()
    public approved = false
    public isPrivate = false

    /**
     * Indica si la publicación es nueva
     */
    public get isNew () {
        return !this.id
    }

    get createdAtFormat (): string {
        return moment.unix(this.createdAt || 0).format('LLL')
    }

    public getTranslation (locale: string): PostTranslation | null {
        return this.translations.find(t => t.locale.toString() === locale) || null
    }

    /**
     * Comprueba que la publicación contiene traducciones
     */
    public get hasTranslations () {
        return !!this.translations.length
    }

    /**
     * Comprueba si una traducción existe
     *
     * @param locale
     */
    public hasTranslation (locale: string) {
        return this.translations.findIndex(t => t.locale === locale) !== -1
    }

    /**
     * Convierte la información en plano obtenida por el servidor en una entidad
     * Post.
     *
     * @param data
     */
    public static convert (data: {[key: string]: any}): Post {
        const post = new Post()
        post.id = data.id
        post.createdAt = parseInt(data.creation_at, undefined)
        post.updatedAt = parseInt(data.update_at, undefined)

        // Define al autor de la publicación
        post.author.id = data.author.id
        post.author.name = data.author.name

        // Define las etiquetas
        post.tags = data.tags

        // SEO de la publicación
        post.seo = Metadata.deserialize(data.seo || {})

        post.featuredImage = MediaObject.convert(data.featured_image || {})
        post.approved = data.approved || false
        post.isPrivate = data.private || false

        // Define las traducciones
        if (Array.isArray(data.translations)) {
            post.translations = data.translations.map(t => PostTranslation.convert(t))
        }

        return post
    }

    /**
     * Convierte una entidad Post en un objeto plano listo para ser enviado
     * al servidor.
     *
     * @param post
     */
    public static serialize (post: Post): {[key: string]: any} {
        return {
            creator: post.author.id,
            tags: post.tags,
            translations: post.translations.map(t => PostTranslation.serialize(t)),
            seo: post.seo.serialize(),
            featured_image: post.featuredImage.id,
            private: post.isPrivate,
        }
    }
}

/**
 * Traducción de publicación
 *
 * Representa el contenido de una publicación en un determinado idioma o
 * localismo.
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
export class PostTranslation {
    public id = 0
    public title = ''
    public content = ''
    public shortDescription = ''
    public slug = ''

    constructor (public locale: string) {
    }

    public static convert (data: {[key: string]: any}): PostTranslation {
        const translation            = new PostTranslation(data.locale)
        translation.id               = data.id
        translation.title            = data.title
        translation.content          = data.content
        translation.shortDescription = data.short_description
        translation.slug             = data.slug

        return translation
    }

    public static serialize (translation: PostTranslation): {[key: string]: any} {
        return {
            locale: translation.locale,
            content: translation.content,
            title: translation.title,
            slug: translation.slug,
            short_description: translation.shortDescription,
        }
    }
}

/**
 * Etiqueta
 */
export class Tag {
    public id = 0
    public name = ''

    public createAt = new Date()
    public updateAt = new Date()

    /**
     * Indica si la etiqueta es nueva
     */
    public get isNew () {
        return !this.id
    }

    /**
     * Crea una entidad Tag a partir de los datos planos
     *
     * @param data
     */
    public static deserialize (data: KeyValuePair) {
        return Object.assign(new this(), {
            id: data.id,
            name: data.name,
            createdAt: moment(data.created_at, 'DD-MM-YYYY HH:mm:ss'),
            updatedAt: moment(data.updated_at, 'DD-MM-YYYY HH:mm:ss'),
        })
    }

    public serialize () {
        return {
            name: this.name,
        }
    }
}

/**
 * Categoría de publicaciones
 */
export class Category {
    public id: number = 0
    public name: string
    public parent: Category | null = null
    public children: Category[] = []
}

/**
 * Paginación de publicaciones
 */
export class PostPagination {
    public posts: Post[] = []
    public totalPages: number = 0
    public recordsPerPage: number = 0
    public totalRecords: number = 0

    /**
     * Convertir
     *
     * Convierte la información en plano obtenida del servidor en una entidad
     * tipada de paginación de artículos
     *
     * @returns {PostPagination}
     * @param data
     */
    public static convert (data: KeyValuePair): PostPagination {
        const pagination = new PostPagination()
        pagination.totalPages     = data.total_pages
        pagination.totalRecords   = data.total_records
        pagination.recordsPerPage = data.records_per_page
        pagination.posts          = data.posts.map((p: KeyValuePair) => Post.convert(p))

        return pagination
    }
}

/**
 * Filtros para la paginación de publicaciones
 */
export class PostPaginationFilters {
    public totalRecords = 0
    public page = 1
    public recordsPerPage = 20
    public noContent = true
    public tags: string[] = []
    public categories: number[] = []
    public contains = {text: '', inLocale: ''}
}
